import type { ReactNode } from "react";
import { useState } from "react";
import { Container, Grid, Button, Chip, Typography, useMediaQuery } from "@mui/material";
import { routesConfig } from "config/app-config";
import SquareImage from "components/atoms/SquareImage";
import { Authorization } from "@sdeapps/react-core";
import CartActions from "components/molecules/CartActions";
import PageTitle from "components/molecules/PageTitle";
import ApplicationRoles from "constants/ApplicationRoles";
import type Article from "models/Article";
import DesactiveChip from "components/atoms/Chips/DesactiveChip";
import { enqueueSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface ArticleInformationTemplateProps {
  article: Article;
  onToggleActive: () => Promise<void>;
}

function ArticleInformationTemplate({
  article,
  onToggleActive,
}: Readonly<ArticleInformationTemplateProps>): ReactNode {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // TODO à remplacer par un useSnackbarErrorHandler()
  const [isLoading, setIsLoading] = useState(false);

  async function toggleActive(): Promise<void> {
    setIsLoading(true);
    try {
      await onToggleActive();
    } catch (e) {
      enqueueSnackbar(ToastMessages.ERROR_RETRY, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <PageTitle
        title={
          <>
            {article.label} {!article.isActive && <DesactiveChip sx={{ ml: 1 }} />}
          </>
        }
      />
      <Container sx={{ mb: 4 }}>
        <Grid container spacing={2} size={12}>
          <Authorization roles={ApplicationRoles.SSTMANAGER}>
            <Grid container spacing={2} size={12}>
              <Grid>
                <Button
                  href={routesConfig.modifyArticle.getParameterPath(article.id)}
                  variant="contained"
                  startIcon={<EditIcon />}>
                  Modifier l'article
                </Button>
              </Grid>
              <Grid>
                <Button
                  onClick={() => {
                    void toggleActive();
                  }}
                  color="secondary"
                  variant="contained"
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={article.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}>
                  {article.isActive ? "Désactiver" : "Réactiver"} l'article
                </Button>
              </Grid>
            </Grid>
          </Authorization>
          <Grid
            container
            spacing={2}
            size={{ md: 3, xs: 12 }}
            sx={{ alignContent: "center", justifyContent: "center" }}>
            <Grid container size={12} sx={{ justifyContent: "center", position: "relative" }}>
              <SquareImage
                image={article.photo.sasUri}
                width={isSmallScreen ? "120px" : "140px"}
                withBackground
                isArticleActif={article.isActive}
              />
            </Grid>
            {article.informations?.Habilitation != null && (
              <Grid>
                <Chip label={<Typography>Habilitation requise</Typography>} color="error" />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} size={{ md: 9, xs: 12 }}>
            <Grid size={12}>
              <Typography sx={{ whiteSpace: "break-spaces" }}>{article.description}</Typography>
            </Grid>
            <CartActions article={article} alignContent="flex-end" justifyContent="flex-end" />
          </Grid>
          {((Object.keys(article.informations).length > 0 &&
            Object.values(article.informations).some((v, i) => v != null && v !== "")) ||
            (article.metiers != null && article.metiers.length > 0)) && (
            <>
              <Grid size={12}>
                <Typography variant="h3" color="primary">
                  Informations pratiques
                </Typography>
              </Grid>
              {article?.metiers != null && article?.metiers.length > 0 && (
                <Grid container size={12}>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Typography>
                      <strong>Recommandé pour</strong>
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 10 }} container spacing={1}>
                    {article.metiers.map((m) => (
                      <Chip key={m.id} label={m.libelle} size="small" />
                    ))}
                  </Grid>
                </Grid>
              )}
              {Object.keys(article.informations).map(
                (value) =>
                  article.informations[value] !== "" && (
                    <Grid container key={value} size={12}>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <Typography>
                          <strong>{value}</strong>
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 10 }}>
                        <Typography sx={{ whiteSpace: "break-spaces" }}>
                          {article.informations[value]}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
              )}
            </>
          )}
        </Grid>
      </Container>
    </>
  );
}

export default ArticleInformationTemplate;
